import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Lock, RefreshCw, Sparkles, Maximize2, X, Wand2, Square } from "lucide-react";

function MockupCard({ page, index, mockup, allowed, busy, projectId, onGenerate }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!mockup) return;
    let url;
    api.get(`/projects/${projectId}/mockups/${index}/image`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setImgUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [mockup, projectId, index]);

  return (
    <div className="glass rounded-2xl overflow-hidden card-hover flex flex-col" data-testid={`mockup-card-${index}`}>
      <div className="aspect-video bg-white/[0.02] relative flex items-center justify-center border-b border-white/5">
        {imgUrl && !busy ? (
          <>
            <img src={imgUrl} alt={`Maquette ${page.name}`} className="w-full h-full object-cover" data-testid={`mockup-image-${index}`} />
            <button onClick={() => setZoom(true)} className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 hover:bg-black/80 transition-colors" data-testid={`mockup-zoom-${index}`}>
              <Maximize2 size={14} />
            </button>
          </>
        ) : busy ? (
          <div className="flex flex-col items-center gap-2 text-gray-400 text-sm">
            <Loader2 className="animate-spin" size={22} />
            Génération en cours (~20 s)…
          </div>
        ) : mockup ? (
          <Loader2 className="animate-spin text-gray-500" size={20} />
        ) : (
          <ImageIcon size={32} className="text-gray-600" />
        )}
      </div>
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{page.name}</div>
          <div className="text-xs text-gray-400 mt-1 line-clamp-2">{page.purpose}</div>
        </div>
        {allowed ? (
          <button onClick={() => onGenerate(index)} disabled={busy} className="pill-ghost text-xs flex items-center gap-1.5 shrink-0 disabled:opacity-50" data-testid={`generate-mockup-button-${index}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : mockup ? <RefreshCw size={12} /> : <Sparkles size={12} />}
            {mockup ? "Régénérer" : "Générer"}
          </button>
        ) : (
          <Lock size={14} className="text-gray-500 shrink-0 mt-1" />
        )}
      </div>
      {zoom && imgUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setZoom(false)} data-testid="mockup-lightbox">
          <button className="absolute top-4 right-4 p-2 rounded-lg bg-white/10" data-testid="mockup-lightbox-close"><X size={18} /></button>
          <img src={imgUrl} alt={page.name} className="max-w-full max-h-full rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function BatchProgress({ done, total, current, onCancel }) {
  const pct = Math.round((done / total) * 100);
  return (
    <div className="glass rounded-2xl p-5 mb-6 animate-fade-up" data-testid="batch-progress">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="text-sm">
          <span className="font-semibold">Génération de toutes les maquettes</span>
          <span className="text-gray-400"> — {done}/{total}</span>
          {current && <span className="text-gray-500"> · {current}</span>}
        </div>
        <button onClick={onCancel} className="pill-ghost text-xs flex items-center gap-1.5" data-testid="batch-cancel-button">
          <Square size={11} /> Arrêter
        </button>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="batch-progress-bar" />
      </div>
    </div>
  );
}

export default function PageMockups({ projectId, pages }) {
  const [mockups, setMockups] = useState({});
  const [allowed, setAllowed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState({});
  const [batch, setBatch] = useState(null);
  const cancelRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/mockups`);
      setMockups(Object.fromEntries(data.mockups.map((m) => [m.page_index, m])));
      setAllowed(data.allowed);
    } finally {
      setLoaded(true);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const generateOne = async (i, silent = false) => {
    setBusy((b) => ({ ...b, [i]: true }));
    try {
      const { data } = await api.post(`/projects/${projectId}/mockups/${i}`);
      setMockups((prev) => ({ ...prev, [i]: data }));
      if (!silent) toast.success(`Maquette « ${pages[i].name} » générée !`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erreur sur « ${pages[i].name} »`);
      return false;
    } finally {
      setBusy((b) => ({ ...b, [i]: false }));
    }
  };

  const missing = pages.map((_, i) => i).filter((i) => !mockups[i]);
  const targets = missing.length ? missing : pages.map((_, i) => i);

  const generateAll = async () => {
    cancelRef.current = false;
    let done = 0;
    setBatch({ done, total: targets.length, current: pages[targets[0]]?.name });
    for (const i of targets) {
      if (cancelRef.current) break;
      setBatch({ done, total: targets.length, current: pages[i].name });
      await generateOne(i, true);
      done += 1;
      setBatch({ done, total: targets.length, current: pages[i].name });
    }
    setBatch(null);
    toast.success(cancelRef.current ? `Arrêté après ${done} maquette(s)` : `${done} maquette(s) générée(s) !`);
  };

  if (!loaded) return null;

  return (
    <section className="mt-10" data-testid="mockups-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-400">Maquettes visuelles</div>
          <h2 className="text-lg md:text-xl font-bold mt-1">Vos écrans, rendus en image par l'IA</h2>
        </div>
        {allowed ? (
          <button onClick={generateAll} disabled={!!batch || pages.length === 0} className="pill-primary text-sm flex items-center gap-2 disabled:opacity-60" data-testid="generate-all-mockups-button">
            {batch ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {missing.length ? `Tout générer (${missing.length})` : "Tout régénérer"}
          </button>
        ) : (
          <Link to="/tarifs" className="pill-primary text-sm flex items-center gap-2" data-testid="mockups-upgrade-link">
            <Lock size={14} /> Débloquer avec un forfait payant
          </Link>
        )}
      </div>
      {batch && <BatchProgress {...batch} onCancel={() => { cancelRef.current = true; }} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {pages.map((p, i) => (
          <MockupCard key={i} page={p} index={i} mockup={mockups[i]} allowed={allowed} busy={!!busy[i]} projectId={projectId} onGenerate={generateOne} />
        ))}
      </div>
    </section>
  );
}
